/*
 * <find-a-store>
 *
 * Static map build: the map is an uploaded image and each store is an
 * anchor positioned over it, so the section works with JS disabled.
 * This element only adds progressive enhancement:
 *   - client-side filtering of the store list
 *   - hover/focus sync between a card and its pin
 *   - keeping the mobile "View Map" link pointed at the store in view
 *
 * renderMap() is the seam for a future interactive map (Leaflet or
 * similar). Blocks already carry data-latitude / data-longitude, so a
 * tile layer can replace the static image without reshaping the markup.
 */
/*
 * Opening-hours helpers. Times are compared in India Standard Time regardless
 * of where the visitor is, because the stores are in India.
 */
const FIND_A_STORE_TIMEZONE = 'Asia/Kolkata';

/*
 * Accepts "10:00 AM", "10 AM", "10:00", "18:00". Returns minutes since
 * midnight, or null when the text cannot be understood.
 */
function parseStoreTime(value) {
  if (!value) return null;

  const match = String(value)
    .trim()
    .match(/^(\d{1,2})(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = match[2] ? parseInt(match[2], 10) : 0;
  const meridiem = match[3] ? match[3][0].toLowerCase() : null;

  if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes > 59) return null;

  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem === 'p' && hours !== 12) hours += 12;
    if (meridiem === 'a' && hours === 12) hours = 0;
  } else if (hours > 23) {
    return null;
  }

  return hours * 60 + minutes;
}

/* Minutes since midnight right now, in IST. */
function currentMinutesInTimezone() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: FIND_A_STORE_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const get = (type) => {
    const part = parts.find((entry) => entry.type === type);
    return part ? parseInt(part.value, 10) : 0;
  };

  // Some engines format midnight as 24; normalise it to 0.
  return (get('hour') % 24) * 60 + get('minute');
}

/*
 * Works out whether a store is open now. Handles hours that run past midnight
 * (open 20:00, close 02:00) by treating the window as wrapping.
 */
function resolveStoreStatus(openTime, closeTime, nowMinutes) {
  const open = parseStoreTime(openTime);
  const close = parseStoreTime(closeTime);
  if (open === null || close === null || open === close) return null;

  const wraps = close < open;
  const isOpen = wraps
    ? nowMinutes >= open || nowMinutes < close
    : nowMinutes >= open && nowMinutes < close;

  if (isOpen) {
    return { open: true, label: 'Open now', detail: 'Closes at ' + closeTime };
  }

  // Closed: opening is later today only when we have not passed it yet.
  const opensToday = !wraps && nowMinutes < open;
  return {
    open: false,
    label: 'Closed',
    detail: (opensToday ? 'Opens today at ' : 'Opens tomorrow at ') + openTime,
  };
}

class FindAStore extends HTMLElement {
  constructor() {
    super();
    this.searchInput = this.querySelector('[data-find-a-store-search]');
    this.list = this.querySelector('[data-find-a-store-list]');
    this.items = Array.from(this.querySelectorAll('[data-find-a-store-item]'));
    this.pins = Array.from(this.querySelectorAll('[data-find-a-store-pin]'));
    this.emptyMessage = this.querySelector('[data-find-a-store-empty]');
    this.mapCta = this.querySelector('[data-find-a-store-map-cta]');
    this.mapCtaFallback = this.mapCta ? this.mapCta.getAttribute('href') : null;

    this.onSearch = this.onSearch.bind(this);
    this.onCardEnter = this.onCardEnter.bind(this);
    this.onCardLeave = this.onCardLeave.bind(this);
  }

  connectedCallback() {
    if (this.items.length === 0) return;

    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.onSearch);
    }

    this.items.forEach((item) => {
      item.addEventListener('mouseenter', this.onCardEnter);
      item.addEventListener('mouseleave', this.onCardLeave);
      item.addEventListener('focusin', this.onCardEnter);
      item.addEventListener('click', this.onCardEnter);
    });

    this.updateOpeningStatus();
    this.setActive(this.items[0].dataset.storeId);
    this.observeVisibleStore();
    this.renderMap();

    // Re-check on the minute boundary so a card flips without a reload.
    this.statusTimer = window.setInterval(() => this.updateOpeningStatus(), 60000);
  }

  disconnectedCallback() {
    if (this.searchInput) {
      this.searchInput.removeEventListener('input', this.onSearch);
    }
    this.items.forEach((item) => {
      item.removeEventListener('mouseenter', this.onCardEnter);
      item.removeEventListener('mouseleave', this.onCardLeave);
      item.removeEventListener('focusin', this.onCardEnter);
      item.removeEventListener('click', this.onCardEnter);
    });
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.statusTimer) window.clearInterval(this.statusTimer);
  }

  /*
   * Seam for an interactive map. Static build renders nothing here; the
   * uploaded image and the server-rendered pins are already in the DOM.
   */
  renderMap() {
    return null;
  }

  /*
   * Fills each card's status row from its typed opening times. Rows stay
   * hidden when the times are missing or unparseable, so a card never shows a
   * status it cannot stand behind.
   */
  updateOpeningStatus() {
    const nowMinutes = currentMinutesInTimezone();

    this.items.forEach((item) => {
      const row = item.querySelector('[data-find-a-store-status]');
      if (!row) return;

      const status = resolveStoreStatus(
        item.dataset.openTime,
        item.dataset.closeTime,
        nowMinutes
      );

      if (!status) {
        row.hidden = true;
        return;
      }

      const label = row.querySelector('[data-find-a-store-status-label]');
      const detail = row.querySelector('[data-find-a-store-status-detail]');
      if (label) label.textContent = status.label;
      if (detail) detail.textContent = status.detail;

      row.classList.toggle('find-a-store__status--open', status.open);
      row.hidden = false;
    });
  }

  onSearch(event) {
    const query = event.target.value.trim().toLowerCase();
    let visibleCount = 0;

    this.items.forEach((item) => {
      const haystack = (item.dataset.searchTerms || '').toLowerCase();
      const matches = query === '' || haystack.indexOf(query) !== -1;
      item.hidden = !matches;
      if (matches) visibleCount += 1;

      const pin = this.pinFor(item.dataset.storeId);
      if (pin) pin.hidden = !matches;
    });

    if (this.emptyMessage) this.emptyMessage.hidden = visibleCount !== 0;

    const firstVisible = this.items.find((item) => !item.hidden);
    if (firstVisible) this.setActive(firstVisible.dataset.storeId);
  }

  onCardEnter(event) {
    const item = event.currentTarget;
    this.setActive(item.dataset.storeId);
  }

  onCardLeave() {
    // Keep the last hovered pin open so the map never sits empty.
  }

  pinFor(storeId) {
    return this.pins.find((pin) => pin.dataset.storeId === storeId) || null;
  }

  setActive(storeId) {
    if (!storeId) return;
    this.activeStoreId = storeId;

    this.pins.forEach((pin) => {
      pin.classList.toggle('is-active', pin.dataset.storeId === storeId);
    });
    this.items.forEach((item) => {
      item.classList.toggle('is-active', item.dataset.storeId === storeId);
    });

    this.updateMapCta(storeId);
  }

  updateMapCta(storeId) {
    if (!this.mapCta) return;
    const item = this.items.find((entry) => entry.dataset.storeId === storeId);
    const link = item && item.dataset.mapLink;
    const href = link || this.mapCtaFallback;

    if (href) {
      this.mapCta.setAttribute('href', href);
      this.mapCta.removeAttribute('aria-disabled');
    } else {
      this.mapCta.removeAttribute('href');
      this.mapCta.setAttribute('aria-disabled', 'true');
    }
  }

  /*
   * On mobile the list scrolls with the page, so point "View Map" at
   * whichever store is currently on screen.
   */
  observeVisibleStore() {
    if (!('IntersectionObserver' in window)) return;

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) this.setActive(visible.target.dataset.storeId);
      },
      { threshold: [0.25, 0.5, 0.75] }
    );

    this.items.forEach((item) => this.visibilityObserver.observe(item));
  }
}

customElements.define('find-a-store', FindAStore);
